import React from "react";
import { NumericFormat } from "react-number-format";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import { TagIcon } from "@heroicons/react/24/solid";
import { useClient } from "ClientContext";
import getSymbolFromCurrency from "currency-symbol-map";

//translation
import { useTranslation } from "react-i18next";

// Interface for the props
interface PriceBreakdownProps {
  listingId: string
  quote: {
    nightlyPrice?: number;
    nightlyTotal?: number;
    feesItems?: Array<{ title: string; amount: number }>;
    taxesItems?: Array<{ title: string; amount: number }>;
    stayTotal?: number;
    lengthOfStay?: number;
    totalTaxes?: number;
    // Promotion or Coupons Information
    totalPromo?: number;
    promoItems?: {
      name: string;
      value: number;
      amount?: number;
      title?: string;
    }[];
    coupons?: Array<{
      name: string;
      couponCode: string;
      discountType: "PERCENT" | "FLAT";
      discount: number;
    }>;
  };
  default_currency: string;
  showCouponCode: boolean;
  setShowCouponCode: React.Dispatch<React.SetStateAction<boolean>>;
  couponCode: string;
  setCouponCode: React.Dispatch<React.SetStateAction<string>>;
  couponError: string;
  setCouponError: React.Dispatch<React.SetStateAction<string>>;
  applyCoupon: () => void;
  handleFetchQuote?: ({ listingId }: { listingId: string, couponCode?: string }) => void;
  couponErrorState: string | null;
  setCouponErrorState: React.Dispatch<React.SetStateAction<string | null>>;
}

const PriceBreakdown: React.FC<PriceBreakdownProps> = ({
  listingId,
  quote,
  default_currency,
  showCouponCode,
  setShowCouponCode,
  couponCode,
  setCouponCode,
  couponError,
  setCouponError,
  handleFetchQuote,
  setCouponErrorState,
  couponErrorState,
}) => {
  const { t } = useTranslation();
  const { integration_TYPE, } = useClient();
  const applyCoupon = () => {
    if (!couponCode) {
      setCouponError("Please enter a coupon code.");
      return;
    }

    if (handleFetchQuote) {
      handleFetchQuote({ listingId: listingId, couponCode });
    }
    setCouponError("");
    setCouponErrorState(null);
  };
  const currencySign = getSymbolFromCurrency(default_currency);
  const totalFees =
    quote?.feesItems?.reduce((sum, fee) => sum + fee.amount, 0) || 0;

  // Total discount from all applied coupons
  const totalCouponDiscount = Array.isArray(quote?.promoItems)
    ? quote.promoItems.reduce((sum, coupon) => sum - (coupon.amount ?? 0), 0)
    : 0;

  return (
    <div className="w-full bg-gray-50 dark:bg-neutral-800 p-4 rounded-lg shadow-md text-sm mb-2">
      <div className="flex justify-between font-semibold mt-2 mb-1">
        <span>{t('Rate')}</span>
      </div>
      <div className="flex justify-between mb-1">
        <div className="flex items-center">
          <NumericFormat
            value={quote?.nightlyPrice || 0}
            displayType="text"
            thousandSeparator
            prefix={`${default_currency} ${currencySign} `}
            decimalScale={0}
          />
          <span className="mr-1"> X {quote?.lengthOfStay || 0} {t('nights')}</span>
        </div>
        <NumericFormat
          value={quote?.nightlyTotal || 0}
          displayType="text"
          thousandSeparator
          prefix={`${default_currency} ${currencySign}`}
          decimalScale={0}
        />
      </div>



      <div className="flex justify-between font-semibold mt-2 mb-1">
        <span>{t('Fees')}</span>
      </div>
      {quote?.feesItems && quote.feesItems.length > 0 ? (
        quote.feesItems.map((fee, idx) => (
          <div key={idx} className="flex justify-between text-neutral-500">
            <span>{fee.title}</span>
            <NumericFormat
              value={fee.amount}
              displayType="text"
              thousandSeparator
              prefix={`${default_currency} ${currencySign}`}
              decimalScale={0}
            />
          </div>
        ))
      ) : (
        <div className="flex justify-between text-neutral-500">
          <span>No additional fees</span>
          <NumericFormat
            value={0}
            displayType="text"
            thousandSeparator
            prefix={`${default_currency} ${currencySign}`}
            decimalScale={0}
          />
        </div>
      )}

      {/* Applied Coupons Section */}
      {Array.isArray(quote?.promoItems) && quote.promoItems.length > 0 ? (
        <>
          <div className="flex justify-between font-semibold mt-2 mb-1">
            <span>Coupons</span>
          </div>
          {quote.promoItems.map((coupon, idx) => (
            <div
              key={idx}
              className="flex justify-between items-center text-neutral-500"
            >
              <div className="flex items-center space-x-2">
                <TagIcon className="w-5 h-5 text-neutral-500" />
                <span>{coupon.title}</span>
              </div>
              <NumericFormat
                value={
                  coupon.amount
                  //  coupon.value === "PERCENT"
                  //? -(quote?.nightlyTotal || 0) * (coupon.discount / 100): -coupon.discount
                }
                displayType="text"
                thousandSeparator
                prefix={`${default_currency} ${currencySign} `}
                decimalScale={0}
              />
            </div>
          ))}
        </>
      ) : null}

      {/* Subtotal Section */}
      <div className="border-t mt-2 mb-2"></div>
      <div className="flex justify-between font-semibold">
        <span>{t('Subtotal before taxes')}</span>
        <NumericFormat
          value={(quote?.nightlyTotal || 0) + totalFees - totalCouponDiscount}
          displayType="text"
          thousandSeparator
          prefix={`${default_currency} ${currencySign}`}
          decimalScale={0}
        />
      </div>

      {/* Taxes Section */}
      <div className="border-t mt-2 mb-2"></div>
      <div className="flex justify-between font-semibold">
        <span>{t('Total taxes')}</span>
        <NumericFormat
          value={(quote?.totalTaxes || 0)}
          displayType="text"
          thousandSeparator
          prefix={`${default_currency} ${currencySign}`}
          decimalScale={0}
        />
      </div>

      {/* Coupon Code Section */}
      {(integration_TYPE === "guesty" || integration_TYPE === "hostaway") && <div className="mt-4">
        <div className="flex items-center space-x-3">
          <input
            type="checkbox"
            id="toggleCouponCode"
            className="w-5 h-5 cursor-pointer"
            checked={showCouponCode}
            onChange={(e) => {
              setShowCouponCode(e.target.checked);
              if (!e.target.checked) {
                setCouponCode("");
                setCouponError("");
                setCouponErrorState(null);
              }
            }}
          />
          <label
            htmlFor="toggleCouponCode"
            className="text-sm text-neutral-700 dark:text-neutral-300 cursor-pointer"
          >
            {t('I have a coupon code')}
          </label>
        </div>

        {showCouponCode && (
          <div className="mt-2">
            <div className="flex items-center">
              <input
                type="text"
                maxLength={30} // Restrict the maximum length to 15 characters
                className="flex-1 px-4 py-2 border border-neutral-300 rounded-md text-sm dark:bg-neutral-800 dark:text-neutral-300 focus:outline-none focus:ring-0 focus:border-transparent"
                placeholder={t("Enter coupon code")}
                value={couponCode}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value.length <= 30) {
                    // Prevent updating beyond 30 characters
                    setCouponCode(value);
                    setCouponError("");
                    setCouponErrorState(null);
                  } else {
                    setCouponError("the cooupon code is too long");
                  }
                }}
              />
              <ButtonPrimary
                onClick={applyCoupon}
                className="ml-2 h-12 min-h-12"
              >
                {t('Apply')}
              </ButtonPrimary>
            </div>
            {couponError && (
              <div className="text-red-500 text-sm mt-1">{couponError}</div>
            )}
            {couponErrorState && (
              <div className="text-red-500 text-sm mt-1">
                {couponErrorState}
              </div>
            )}
          </div>
        )}
      </div>}

      <div className="border-t mt-3 pt-3 flex justify-between font-bold text-lg">
        <span>Total</span>
        <NumericFormat
          value={quote?.stayTotal || 0}
          displayType="text"
          thousandSeparator
          prefix={`${default_currency} ${currencySign}`}
          decimalScale={0}
        />
      </div>
    </div>
  );
};

export default PriceBreakdown;
